import React, { useEffect, useMemo } from 'react';
import DataGrid, {
  Column,
  Pager,
  Paging,
  SearchPanel,
} from 'devextreme-react/data-grid';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Grid } from '@material-ui/core';
import Breadcrumb from '~/components/CustomUI/CustomBreadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { getBoletim } from '~/store/modules/boletim/actions';

const ROW_ALTERNATION_COLOR = '#f3f0f0';

const useStyles = makeStyles({
  tituloFuncionalidade: {
    textTransform: 'uppercase',
    color: '#3E98C7',
    fontWeight: 'bold',
    marginBottom: '30px',
  },
  grids: {
    marginTop: '15px',
  },
  showFiltros: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  headerCell: {
    '& > div': {
      height: '100%',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
    },
  },
  dataGridRoot: {
    '& .dx-datagrid-rowsview .dx-data-row.dx-row-alt > td': {
      backgroundColor: `${ROW_ALTERNATION_COLOR} !important`,
    },
    '& .dx-datagrid-rowsview .dx-data-row > td.pkg-separator': {
      borderRight: '2px solid #bdbdbd !important',
    },
    '& .dx-datagrid-headers .dx-header-row > td.pkg-separator': {
      borderRight: '2px solid #bdbdbd !important',
    },
  },
});

export default function Boletim() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { cache, data } = useSelector((state) => state.boletim);

  useEffect(() => {
    if (!cache || new Date() - new Date(cache) > 60000) {
      dispatch(getBoletim());
    }
  }, [cache, data]); //eslint-disable-line

  const { uniquePackages, transformedData } = useMemo(() => {
    if (!data || data.length === 0) {
      return { uniquePackages: [], transformedData: [] };
    }

    const packages = [...new Set(data.map((item) => item.pacote))];

    const groupedByMateria = data.reduce((acc, item) => {
      if (!acc[item.materia]) {
        acc[item.materia] = { materia: item.materia };
      }

      acc[item.materia][`${item.pacote}_nota`] = item.nota
        ? Math.round(item.nota * 10) / 10
        : '-';
      acc[item.materia][`${item.pacote}_faltas`] = item.faltas || '-';
      acc[item.materia][`${item.pacote}_media_padrao`] =
        typeof item.media_padrao !== 'undefined' ? item.media_padrao : null;

      return acc;
    }, {});

    const transformed = Object.values(groupedByMateria).map((subject) => {
      // Get all grades for this subject
      const grades = packages.map((pkg) => subject[`${pkg}_nota`]);

      const allGradesAvailable = grades.every((grade) => grade !== '-');

      if (allGradesAvailable) {
        const numericGrades = grades.map((grade) => Number(grade));
        const average =
          numericGrades.reduce((sum, grade) => sum + grade, 0) /
          numericGrades.length;
        subject.media_final = Math.round(average * 10) / 10;
      } else {
        subject.media_final = '-';
      }

      return subject;
    });

    return { uniquePackages: packages, transformedData: transformed };
  }, [data]);

  return (
    <>
      <Breadcrumb funcionalidade="Boletim" />
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="h6" className={classes.tituloFuncionalidade}>
            Boletim
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.grids}>
        <DataGrid
          dataSource={transformedData}
          allowColumnReordering
          showBorders
          rowAlternationEnabled
          className={classes.dataGridRoot}
          noDataText="Sem dados disponíveis"
        >
          <SearchPanel visible placeholder="Filtrar" />
          <Paging defaultPageSize={10} />
          <Pager showInfo infoText="Página {0} de {1} ({2} registros)" />

          <Column
            caption="MATÉRIA"
            dataField="materia"
            alignment="center"
            width={400}
            allowSorting
            headerCellRender={() => (
              <div
                style={{
                  height: '100%',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                MATÉRIA
              </div>
            )}
          />

          {uniquePackages.map((packageName) => {
            return (
              <Column
                key={packageName}
                caption={packageName}
                cssClass={`${classes.headerCell} pkg-separator`}
                headerCellRender={() => (
                  <div
                    style={{
                      fontWeight: 'bold',
                    }}
                  >
                    {packageName}
                  </div>
                )}
              >
                <Column
                  caption="Nota"
                  dataField={`${packageName}_nota`}
                  alignment="center"
                  width={100}
                  cellRender={({ data: rowData }) => {
                    const nota = rowData[`${packageName}_nota`];
                    const mediaPadrao = rowData[`${packageName}_media_padrao`];
                    const isNumber =
                      nota !== '-' && !Number.isNaN(Number(nota));
                    const isBelow =
                      isNumber &&
                      mediaPadrao != null &&
                      !Number.isNaN(Number(mediaPadrao)) &&
                      Number(nota) < Number(mediaPadrao);
                    return (
                      <span
                        style={
                          isBelow
                            ? { color: 'red', fontWeight: 600 }
                            : undefined
                        }
                      >
                        {nota}
                      </span>
                    );
                  }}
                />
                <Column
                  caption="Faltas"
                  dataField={`${packageName}_faltas`}
                  alignment="center"
                  width={100}
                  cssClass="pkg-separator"
                />
              </Column>
            );
          })}

          <Column
            caption="MÉDIA FINAL"
            dataField="media_final"
            alignment="center"
            width={120}
            cssClass={classes.headerCell}
            headerCellRender={() => (
              <div
                style={{
                  fontWeight: 'bold',
                }}
              >
                MÉDIA FINAL
              </div>
            )}
          />
        </DataGrid>
      </Grid>
    </>
  );
}
